import { OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, inject, Inject, OnInit } from '@angular/core';
import { Observable, timer, animationFrameScheduler, fromEvent, merge } from 'rxjs';
import { map, takeWhile, startWith } from 'rxjs/operators';
import { FipSessionService } from './fip-session.service';

@Component({
  standalone: true,
  imports: [AsyncPipe],
  selector: 'lib-fip-session-timeout',
  template: `
    <div class="modal">
      <div class="modal-window">
        <finra-modal size="lg" theme="warning" [showCloseIcon]="false">
          <div slot="title">Session Timeout</div>
          <div class="modalBody">
            <div>{{ message }} {{ remainingTime$ | async }}</div>
          </div>
          @if (!forceLogout) {
            <div class="commands" slot="commands">
              <finra-button [theme]="'secondary'" (click)="close(false)">
                <span style="min-width: 47px; text-align: center;display: block;">Logout</span> </finra-button
              >,
              <finra-button [theme]="'primary'" (click)="close(true)">
                <span style="min-width: 47px; text-align: center;display: block;">Stay</span>
              </finra-button>
            </div>
          }
        </finra-modal>
      </div>
    </div>
    <div class="modal-background"></div>
  `,
  styles: [
    `
      .modal .modal-window {
        position: fixed;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 102;
      }

      .modal-background {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: #000000;
        opacity: 0.6;
        z-index: 101;
      }
    `,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FipSessionTimeoutComponent implements OnInit {
  remainingTime$: Observable<string | undefined> | undefined;
  get message() {
    return this.forceLogout
      ? `Unable to refresh your session. Please login again in`
      : `Due to inactivity, your session will timeout in`;
  }
  fipSessionService: FipSessionService;
  private document = inject(DOCUMENT);

  constructor(
    @Inject('forceLogout') public forceLogout: boolean,
    @Inject('remainingTime') private remainingTime: number,
    @Inject('overlayRef') private overlayRef: OverlayRef,
  ) {
    this.fipSessionService = inject(FipSessionService);
  }

  ngOnInit(): void {
    const startTime = Date.now();
    const visibilityChange$ = fromEvent(this.document, 'visibilitychange').pipe(startWith(null));

    this.remainingTime$ = merge(timer(0, 1000, animationFrameScheduler), visibilityChange$).pipe(
      map(() => {
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, this.remainingTime - elapsed);
        if (remainingTime <= 0) {
          this.close(false);
          return undefined;
        }
        return this.formatTime(remainingTime);
      }),
      takeWhile((result) => result !== undefined, true),
    );
  }

  close(data: boolean) {
    this.overlayRef.dispose();
    this.fipSessionService.resolveSessionUserAction(data);
  }

  formatTime(value: number): string {
    const totalSeconds = value / 1000;
    const minutes: number = Math.floor(totalSeconds / 60);
    const seconds: number = Math.floor(totalSeconds - minutes * 60);

    return minutes ? `${minutes} minutes and ${seconds} seconds.` : `${seconds} seconds.`;
  }
}
